import { type ThunkAction, configureStore, type ThunkDispatch } from '@reduxjs/toolkit';
import appSlice from './appSlice';
import authReducer from './authSlice';
import memberReducer from './memberSlice';
import userReducer from './userSlice';
import loanReducer from './loanSlice';
import loansReducer from './loans';
import tellerReducer from './tellerSlice';
import savingReducer from './savingSlice';
import savingsReducer from './savings';
import membersReducer from './members';
import groupsReducer from './../modules/Groups/slices/groups';
import systemReducer from './system';
import viewMemberReducer from './viewMemberSlice';
import viewGroupReducer from './../modules/Groups/slices/viewGroup';
import markerChecker from '../modules/MarkerChecker/slices/listMc';
import staffReducer from '../modules/Staff/slices/staff';
import usersReducer from '../modules/Users/slices/user';
import rolesReducer from '../modules/Roles/slices/role';
import brandSlice from './brandSlice';
import loanProductsReducer from '../modules/Products/Loans/slices/loanProducts';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { type UnknownAsyncThunkAction } from '@reduxjs/toolkit/dist/matchers';

export const store = configureStore({
	reducer: {
		app: appSlice,
		auth: authReducer,
		member: memberReducer,
		viewMember: viewMemberReducer,
		user: userReducer,
		savings: savingReducer,
		saving: savingsReducer,
		loan: loanReducer,
		loans: loansReducer,
		groups: groupsReducer,
		viewGroup: viewGroupReducer,
		teller: tellerReducer,
		members: membersReducer,
		system: systemReducer,
		markerChecker: markerChecker,
		staff: staffReducer,
		users: usersReducer,
		roles: rolesReducer,
		loanProducts: loanProductsReducer,
		brand: brandSlice,
	},
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({
			serializableCheck: {
				// Ignore these action types
				// ignoredActions: ["till/setNewTillTransaction"],
				// Ignore these paths in the state
				ignoredPaths: [
					'app.permDialog.callback',
					'member.memberInfo.memberStatus.dateOfBirth',
					'member.memberInfo.newMember.profileImage',
					'member.memberInfo.memberDocuments.memberShipApplication.file',
					'member.memberInfo.memberDocuments.kraPinCertificate.file',
					'member.memberInfo.memberDocuments.signature.file',
					'member.memberInfo.memberDocuments.salarySlip.file',
					'member.memberInfo.memberDocuments.passportPhoto.file',
					'member.memberInfo.memberDocuments.frontId.file',
					'member.memberInfo.memberDocuments.backId.file',
				],
			},
		}),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = (): ThunkDispatch<RootState, any, any> => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = ThunkAction<
	ReturnType,
	RootState,
	unknown,
	UnknownAsyncThunkAction
>;
